import { useEffect, useState, type FormEvent } from 'react';
import { Plus, AlertTriangle, Users, Calendar } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { useAssignedByMe, useAssignTask, useColleagues } from '@/hooks/useTasks';
import {
  TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, TASK_STATUS_COLORS,
  type TaskPriority, type TaskStatus,
} from '@/types';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { can } from '@/utils/permissions';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  basse:   'var(--tx-3)',
  moyenne: 'var(--p)',
  haute:   'var(--danger)',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '';

const isOverdue = (iso: string | null, status: TaskStatus) =>
  status !== 'terminee' && status !== 'ajournee' && iso && new Date(iso).getTime() < new Date().setHours(0, 0, 0, 0);

/**
 * Panneau "Mon équipe" : liste les tâches que le responsable hiérarchique a
 * attribuées + bouton pour en créer de nouvelles via <AssignTaskDialog>.
 * Visible uniquement pour les rôles DAF/DG/manager/admin_holding.
 */
export const AssignedTasksPanel = () => {
  const { user } = useAuthStore();
  const { data: tasks = [], isLoading } = useAssignedByMe();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!user || !can(user.role, 'tasks.assign')) return null;

  // KPI mini : 4 compteurs par statut
  const counts = {
    en_attente: tasks.filter((t) => t.status === 'en_attente').length,
    en_cours:   tasks.filter((t) => t.status === 'en_cours').length,
    terminee:   tasks.filter((t) => t.status === 'terminee').length,
    ajournee:   tasks.filter((t) => t.status === 'ajournee').length,
  };
  const openCount = counts.en_attente + counts.en_cours;

  return (
    <>
      <div className="ds-bp">
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={16} style={{ color: 'var(--p)' }} />
              Tâches attribuées à mon équipe
            </div>
            <div className="ds-bp-s">{openCount} en cours · {counts.terminee} terminée(s) · {counts.ajournee} ajournée(s)</div>
          </div>
          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setDialogOpen(true)}>
            <Plus size={14} /> Attribuer
          </button>
        </div>
        <div className="ds-bp-b" style={{ padding: 14 }}>
          {isLoading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--tx-3)' }}>Chargement…</div>
          ) : tasks.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--tx-3)' }}>
              Aucune tâche attribuée. Cliquer sur <strong>Attribuer</strong> pour en créer une.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {tasks.map((t) => {
                const overdue = isOverdue(t.dueDate, t.status);
                const isClosed = t.status === 'terminee' || t.status === 'ajournee';
                const statusColor = TASK_STATUS_COLORS[t.status];
                return (
                  <div
                    key={t.id}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 'var(--r-sm)',
                      background: isClosed ? 'var(--bg-deep)' : 'var(--surface)',
                      border: `1px solid ${overdue ? 'var(--danger)' : 'var(--bdr-2)'}`,
                      borderLeft: `4px solid ${statusColor}`,
                      opacity: isClosed ? 0.7 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 500,
                          color: isClosed ? 'var(--tx-3)' : 'var(--tx)',
                          textDecoration: t.status === 'terminee' ? 'line-through' : 'none',
                          fontStyle: t.status === 'ajournee' ? 'italic' : 'normal',
                        }}>{t.title}</div>
                        <div style={{ fontSize: 11, color: overdue ? 'var(--danger)' : 'var(--tx-3)', display: 'flex', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
                          <span>→ {t.user?.fullName ?? '?'}{t.filiale && t.filiale.filialeKey !== user.filialeCode ? ` (${t.filiale.name})` : ''}</span>
                          {t.dueDate && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <Calendar size={11} /> {fmtDate(t.dueDate)} {overdue && '⚠ en retard'}
                            </span>
                          )}
                          {t.priority !== 'moyenne' && (
                            <span style={{ color: PRIORITY_COLOR[t.priority], fontWeight: 600 }}>{TASK_PRIORITY_LABELS[t.priority]}</span>
                          )}
                        </div>
                      </div>
                      {/* Badge status temps réel — couleur reflète l'avancement déclaré par le destinataire */}
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#fff',
                          background: statusColor,
                          padding: '3px 8px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          letterSpacing: .3,
                        }}
                        title={`Statut déclaré par ${t.user?.fullName ?? 'destinataire'}`}
                      >
                        {TASK_STATUS_LABELS[t.status].toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <AssignTaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

// ── Dialog d'attribution ───────────────────────────────────────────────────

const AssignTaskDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { data: colleagues = [], isLoading: loadingColleagues } = useColleagues();
  const assign = useAssignTask();
  const { user } = useAuthStore();

  const [userId, setUserId]     = useState('');
  const [title, setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('moyenne');
  const [dueDate, setDueDate]   = useState('');
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUserId(''); setTitle(''); setDescription(''); setPriority('moyenne'); setDueDate(''); setError(null);
  }, [open]);

  const targetColleague = colleagues.find((c) => c.id === userId);
  const isCrossFiliale = user?.role === 'admin_holding' && targetColleague && targetColleague.tenantKey;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId || !title.trim()) return;
    setError(null);
    try {
      await assign.mutateAsync({
        userId,
        targetFilialeKey: isCrossFiliale ? targetColleague?.tenantKey : undefined,
        title:       title.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate:     dueDate || undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Attribuer une tâche" subtitle="Cocher d'abord un collaborateur, puis remplir la tâche" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Field label="Collaborateur *">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="ds-fi" required style={{ width: '100%' }}>
            <option value="">{loadingColleagues ? 'Chargement…' : '— Sélectionner un collaborateur —'}</option>
            {colleagues.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullName} ({c.role}){user?.role === 'admin_holding' && c.tenantKey ? ` · ${c.tenantKey}` : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Titre *">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Préparer le rapport mensuel…" />
        </Field>

        <Field label="Description (optionnel)">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Échéance">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
          <Field label="Priorité">
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((p) => (
                <option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!userId || !title.trim() || assign.isPending}>
            Attribuer la tâche
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
